import { createColumnHelper } from '@tanstack/react-table';
import { useMemo } from 'react';

import { PortainerSelect, Option } from '@@/form-components/PortainerSelect';

import { NamespaceSelect } from './NamespaceSelect';
import { KubernetesUnifiedAccess } from './types';

const columnHelper = createColumnHelper<KubernetesUnifiedAccess>();

// Namespaced roles that require namespace specification
const NAMESPACE_ROLE_IDS = [3, 4, 6]; // Standard, Read-only, Namespace Operator

interface GetColumnsDeps {
  accessList: KubernetesUnifiedAccess[];
  onChange: (key: 'accessList', value: KubernetesUnifiedAccess[]) => void;
  roleOptions: Option<number>[];
}

export function useColumns({
  accessList,
  onChange,
  roleOptions,
}: GetColumnsDeps) {
  const availableNamespaces = useMemo(
    () => Array.from(new Set(accessList.flatMap((a) => a.namespaces || []))),
    [accessList]
  );
  return [
    columnHelper.accessor('Name', {
      header: 'Name',
      cell: ({ row }) => row.original.Name,
      meta: { width: '15%', className: 'overflow-hidden' },
    }),
    columnHelper.accessor('Type', {
      header: 'Type',
      cell: ({ getValue }) => {
        const type = getValue<string>();
        return type.charAt(0).toUpperCase() + type.slice(1);
      },
      meta: { width: '15%', className: 'overflow-hidden' },
    }),
    columnHelper.accessor('Role', {
      header: 'Role',
      id: 'role',
      cell: ({ row }) => (
        <RoleCell
          item={row.original}
          roleOptions={roleOptions}
          onChangeRole={handleRoleChange}
        />
      ),
      meta: { width: '30%', className: 'overflow-hidden' },
    }),
    columnHelper.accessor('namespaces', {
      header: 'Namespaces',
      id: 'namespaces',
      cell: ({ row }) => (
        <NamespacesCell
          item={row.original}
          onChange={handleNamespacesChange}
          availableNamespaces={availableNamespaces}
        />
      ),
      meta: { width: '40%', className: 'overflow-hidden' },
    }),
  ];

  function updateAccess(
    item: KubernetesUnifiedAccess,
    updater: (prev: KubernetesUnifiedAccess) => KubernetesUnifiedAccess
  ) {
    const newAccessList = [...accessList];
    const index = newAccessList.findIndex(
      (access) => access.Type === item.Type && access.Id === item.Id
    );
    if (index !== -1) {
      newAccessList[index] = updater(newAccessList[index]);
      onChange('accessList', newAccessList);
    }
  }

  function handleRoleChange(item: KubernetesUnifiedAccess, newRoleId: number) {
    const roleName =
      roleOptions.find((r) => r.value === newRoleId)?.label || '';
    const wasNamespaceRole = NAMESPACE_ROLE_IDS.includes(item.Role.Id);
    const isNewNamespaceRole = NAMESPACE_ROLE_IDS.includes(newRoleId);

    updateAccess(item, (prev) => ({
      ...prev,
      Role: { Id: newRoleId, Name: roleName },
      namespaces: (() => {
        if (!isNewNamespaceRole) return undefined;
        return wasNamespaceRole ? item.namespaces : [];
      })(),
    }));
  }

  function handleNamespacesChange(
    item: KubernetesUnifiedAccess,
    newNamespaces: string[]
  ) {
    updateAccess(item, (prev) => ({ ...prev, namespaces: newNamespaces }));
  }
}

function RoleCell({
  item,
  roleOptions,
  onChangeRole,
}: {
  item: KubernetesUnifiedAccess;
  roleOptions: Option<number>[];
  onChangeRole: (item: KubernetesUnifiedAccess, newRoleId: number) => void;
}) {
  return (
    <div className="min-w-max">
      <PortainerSelect<number>
        value={item.Role.Id}
        onChange={(value) => onChangeRole(item, value || 0)}
        options={roleOptions}
        placeholder="Select a role"
        bindToBody
        data-cy={`role-select-${item.Type}-${item.Id}`}
      />
    </div>
  );
}

function NamespacesCell({
  item,
  onChange,
  availableNamespaces,
}: {
  item: KubernetesUnifiedAccess;
  onChange: (item: KubernetesUnifiedAccess, namespaces: string[]) => void;
  availableNamespaces: string[];
}) {
  const hasNamespaceAccess = NAMESPACE_ROLE_IDS.includes(item.Role.Id);
  if (!hasNamespaceAccess) {
    return <span className="text-muted">-</span>;
  }

  return (
    <NamespaceSelect
      selectedNamespaces={item.namespaces || []}
      onChange={(namespaces) => onChange(item, namespaces)}
      placeholder="e.g. default, production"
      data-cy={`namespaces-input-${item.Type}-${item.Id}`}
      namespaces={availableNamespaces}
    />
  );
}
